<script setup lang="ts">
import RadioButton from 'primevue/radiobutton';
import { FieldGroup } from '@neon-one/neon-circuit';
import Checkbox from 'primevue/checkbox';
import { LotKindSchema } from '@neon-one/auctions-types';
import type { LotNoun } from '../../format';

import { OFFER_WINDOW_LABEL } from '../../reassign-consequences';
import { ReassignMode, type ReassignModeValue } from '../../item-actions';

withDefaults(
    defineProps<{
        mode: ReassignModeValue; notify: boolean; noun?: LotNoun; unsold?: boolean; enhanced?: boolean;
        archived?: boolean;
    }>(),
    { noun: LotKindSchema.enum.item, unsold: false, enhanced: false, archived: false },
);

const emit = defineEmits<{ 'update:mode': [ReassignModeValue]; 'update:notify': [boolean] }>();
</script>

<template>
    <FieldGroup legend="How" for="ri-mode">
        <div class="ri__choices">
            <!-- An offer is accepted on the bidder site, which an archived auction no longer has. -->
            <div
                v-if="!archived"
                class="app-checkbox-group ri__mode"
                :class="{ 'ri__mode--selected': mode === ReassignMode.SendOffer }"
                data-testid="ri__mode-offer"
            >
                <RadioButton
                    :model-value="mode"
                    :value="ReassignMode.SendOffer"
                    input-id="ri-mode-offer"
                    name="ri-mode"
                    @update:model-value="emit('update:mode', ReassignMode.SendOffer)"
                />
                <label
                    class="app-field-label ri__mode-text"
                    for="ri-mode-offer"
                >
                    <span class="ri__mode-title">{{ unsold ? 'Offer to bidder' : 'Send offer' }}</span>
                    <span class="ri__mode-desc text-caption">
                        They get {{ OFFER_WINDOW_LABEL }} to accept at that price. Nothing is owed until they
                        do.
                    </span>
                </label>
            </div>
            <div
                class="app-checkbox-group ri__mode"
                :class="{ 'ri__mode--selected': mode === ReassignMode.ReassignNow }"
                data-testid="ri__mode-now"
            >
                <RadioButton
                    :model-value="mode"
                    :value="ReassignMode.ReassignNow"
                    input-id="ri-mode-now"
                    name="ri-mode"
                    @update:model-value="emit('update:mode', ReassignMode.ReassignNow)"
                />
                <label
                    class="app-field-label ri__mode-text"
                    for="ri-mode-now"
                >
                    <span class="ri__mode-title">{{ unsold ? 'Sell to bidder' : 'Reassign now' }}</span>
                    <span class="ri__mode-desc text-caption">
                        <template v-if="enhanced">
                            The {{ noun }} is theirs right away. You choose how to collect below.
                        </template>
                        <template v-else>
                            They get the {{ noun }} right away, on an invoice awaiting payment.
                        </template>
                    </span>
                </label>
            </div>
        </div>
    </FieldGroup>

    <!-- An offer always tells the person it is being offered to, so the opt-out
              is a Reassign-now choice only. -->
    <label
        v-if="mode === ReassignMode.ReassignNow && !enhanced && !archived"
        class="ri__notify"
        for="ri-notify"
        data-testid="ri__notify"
    >
        <Checkbox
            :model-value="notify"
            binary
            input-id="ri-notify"
            @update:model-value="emit('update:notify', !!$event)"
        />
        <span class="ri__notify-text">
            <span>Notify affected bidders</span>
            <span class="text-caption ri__muted">
                Covers the new holder, the previous one, and anyone whose pending offer this replaces.
            </span>
        </span>
    </label>
</template>

<style scoped>
.ri__choices {
    display: flex;
    flex-direction: column;
    gap: var(--app-space-8);
}
.ri__muted {
    margin: 0;
    color: var(--app-text-muted);
}
.ri__mode {
    display: flex;
    align-items: flex-start;
    gap: var(--app-space-12);
    padding: var(--app-space-12);
    border: var(--app-border-width-default) solid var(--app-border-muted);
    border-radius: var(--app-radius-md);
    cursor: pointer;
}
/* §5.4 selected mode card */
.ri__mode--selected {
    border-color: var(--app-border-active);
    background: var(--app-highlight-background);
}
.ri__mode-text {
    display: flex;
    flex: 1;
    min-width: 0;
    cursor: pointer;
    flex-direction: column;
    gap: var(--app-space-4);
}
.ri__mode-title {
    font-weight: var(--app-font-weight-semibold);
}
.ri__mode-desc {
    color: var(--app-text-primary);
}
.ri__notify {
    display: flex;
    align-items: flex-start;
    gap: var(--app-space-12);
    cursor: pointer;
}
.ri__notify-text {
    display: flex;
    flex-direction: column;
    gap: var(--app-space-4);
}
</style>
